// LE PAVAGE DE PENROSE (P3, losanges fin et épais), sans GPU, testable en Node :
// la déflation des triangles de Robinson, les arcs de Conway, le marquage
// des arêtes d'un niveau à l'autre, les fleurs à symétrie 5.
//
// Un triangle = la moitié d'un losange : t = 0, le triangle d'or aigu
// (36° à l'apex A, 72° en B et C) est la moitié d'un losange FIN ; t = 1, le
// gnomon obtus (108° en A, 36° en B et C) la moitié d'un losange ÉPAIS. Le
// losange = le triangle + son miroir par rapport à la base BC : les JAMBES AB
// et AC sont les arêtes du losange, la BASE BC une diagonale (jamais tracée).
//
// LA DÉFLATION (Robinson) : chaque triangle se coupe en 2 ou 3 triangles
// φ fois plus petits ; dix triangles aigus en rosace autour de l'origine
// donnent, après une déflation, un vrai pavage de Penrose à symétrie
// d'ordre 5 (l'origine est un « soleil » : cinq losanges épais) et le
// restent à chaque niveau. Les coordonnées sont multipliées par φ à chaque
// niveau (les jambes gardent la longueur 1 : précision stable pour un zoom
// sans fin) et les triangles sortis du cercle de vue sont abandonnés.
//
// LES ARCS DE CONWAY : sur le losange fin, deux arcs centrés aux sommets
// aigus (A et A', rayon 1/φ) ; sur l'épais, deux arcs centrés aux sommets de
// 72° (B : rayon 1/φ, C : rayon 1/φ²). Rayons trouvés en exigeant que les
// points de partage coïncident sur chaque arête partagée du pavage (seules
// deux règles le font, complémentaires ; tools/test-penrose.mjs le vérifie) :
// les arcs se raccordent en courbes continues, ce qui est exactement la règle
// de raccord de Penrose.

export const PHI = (1 + Math.sqrt(5)) / 2;
export const INV = 1 / PHI, INV2 = 1 / (PHI * PHI);

// la rosace de départ : dix triangles aigus autour de l'origine, alternés en miroir
export function wheel() {
  const out = [];
  for (let i = 0; i < 10; i++) {
    let b = [Math.cos((2 * i - 1) * Math.PI / 10), Math.sin((2 * i - 1) * Math.PI / 10)];
    let c = [Math.cos((2 * i + 1) * Math.PI / 10), Math.sin((2 * i + 1) * Math.PI / 10)];
    if (i % 2 === 0) { const s = b; b = c; c = s; }
    out.push(make(0, [0, 0], b, c, null));
  }
  return out;
}
export function make(t, A, B, C, parent) {
  const cx = (A[0] + B[0] + C[0]) / 3, cy = (A[1] + B[1] + C[1]) / 3;
  const chir = (B[0] - A[0]) * (C[1] - A[1]) - (B[1] - A[1]) * (C[0] - A[0]) > 0 ? 1 : -1;
  return { t, A, B, C, cx, cy, chir, parent };
}
function lerp(P, Q, k) { return [P[0] + (Q[0] - P[0]) * k, P[1] + (Q[1] - P[1]) * k]; }

// un niveau de déflation ; scale : les coordonnées sont multipliées (φ pour
// garder des jambes de longueur 1) ; keep(tri) : le filtre de vue
export function subdivide(tris, scale, keep) {
  const out = [];
  const s = scale || 1;
  const S = (P) => [P[0] * s, P[1] * s];
  for (const tr of tris) {
    const A = S(tr.A), B = S(tr.B), C = S(tr.C);
    const parent = { t: tr.t, A, B, C, chir: tr.chir };
    if (tr.t === 0) {
      const P = lerp(A, B, INV);
      push(out, make(0, C, P, B, parent), keep);
      push(out, make(1, P, C, A, parent), keep);
    } else {
      const Q = lerp(B, A, INV), R = lerp(B, C, INV);
      push(out, make(1, R, C, A, parent), keep);
      push(out, make(1, Q, R, B, parent), keep);
      push(out, make(0, R, Q, A, parent), keep);
    }
  }
  return out;
}
function push(out, tr, keep) { if (!keep || keep(tr)) out.push(tr); }

// le filtre de vue : un cercle de rayon r autour de l'origine (centre de composition)
export function withinRadius(r) { const r2 = (r + 1.2) * (r + 1.2); return (tr) => tr.cx * tr.cx + tr.cy * tr.cy < r2; }

// LES ARCS : [centre, rayon] x 2 par triangle (le second rayon vaut 0 pour le
// triangle aigu, qui n'a qu'un arc, à l'apex). La règle des rayons (voir en tête)
// est fixée par ARC_RULE ; tools/test-penrose.mjs vérifie qu'elle est la seule
// à raccorder les arcs sur tout le pavage.
// (indépendante de la chiralité : les deux moitiés d'un losange portent les mêmes rayons ;
// l'autre famille de Conway est la règle complémentaire, rayons 1 - r)
export const ARC_RULE = { thinApex: { 1: INV, '-1': INV }, thickB: { 1: INV, '-1': INV }, thickC: { 1: INV2, '-1': INV2 } };
export const ARC_RULE_DUAL = { thinApex: { 1: INV2, '-1': INV2 }, thickB: { 1: INV2, '-1': INV2 }, thickC: { 1: INV, '-1': INV } };
export function arcsOf(tr, rule) {
  const R = rule || ARC_RULE;
  if (tr.t === 0) return [[tr.A, R.thinApex[tr.chir]], [tr.A, 0]];
  return [[tr.B, R.thickB[tr.chir]], [tr.C, R.thickC[tr.chir]]];
}
// le point de partage de chaque jambe (AB, AC) : où l'arc coupe l'arête
export function divisionPoints(tr, rule) {
  const [a1, a2] = arcsOf(tr, rule);
  if (tr.t === 0) return { AB: lerp(tr.A, tr.B, a1[1]), AC: lerp(tr.A, tr.C, a1[1]) };
  return { AB: lerp(tr.B, tr.A, a1[1]), AC: lerp(tr.C, tr.A, a2[1]) };
}

// LES CÔTÉS D'UN ENFANT : chaque côté (AB, AC, BC) est-il porté par une
// jambe du parent (une arête du niveau précédent) ? Les côtés portés par la
// base du parent (une diagonale) ou intérieurs sont NOUVEAUX.
export function sideFlags(tr) {
  const p = tr.parent;
  if (!p) return [1, 1, 0];
  const on = (P, Q, U, V) => onSegment(P, U, V) && onSegment(Q, U, V);
  const old = (P, Q) => (on(P, Q, p.A, p.B) || on(P, Q, p.A, p.C)) ? 1 : 0;
  return [old(tr.A, tr.B), old(tr.A, tr.C), old(tr.B, tr.C)];
}
function onSegment(P, U, V) {
  const ux = V[0] - U[0], uy = V[1] - U[1], px = P[0] - U[0], py = P[1] - U[1];
  const L2 = ux * ux + uy * uy;
  const cross = ux * py - uy * px;
  if (cross * cross > 1e-10 * L2) return false;
  const d = (px * ux + py * uy) / L2;
  return d > -1e-6 && d < 1 + 1e-6;
}

// LES FLEURS : les sommets où dix gnomons posent leur angle de 36° (cinq losanges
// épais : « soleil » ou « étoile » du pavage). Renvoie, par triangle, le centre
// de sa fleur ou null.
export function flowers(tris) {
  const key = (P) => Math.round(P[0] * 512) + ',' + Math.round(P[1] * 512);
  const count = new Map();
  for (const tr of tris) if (tr.t === 1) for (const P of [tr.B, tr.C]) { const k = key(P); count.set(k, (count.get(k) || 0) + 1); }
  const centers = new Map();
  for (const [k, n] of count) if (n === 10) centers.set(k, true);
  let n = 0;
  const out = tris.map((tr) => {
    if (tr.t !== 1) return null;
    for (const P of [tr.B, tr.C]) if (centers.has(key(P))) { n++; return P; }
    return null;
  });
  out.count = centers.size;
  return out;
}

// un pavage prêt pour l'écran : `levels` déflations de la rosace, filtrées par
// le rayon de vue (en unités du niveau courant : jambes = 1)
export function buildTiling(levels, viewRadius) {
  let tris = wheel();
  for (let k = 1; k <= levels; k++) {
    const r = viewRadius * Math.pow(PHI, k - levels);     // le rayon de vue, dans les unités du niveau k
    tris = subdivide(tris, PHI, withinRadius(r));
  }
  return tris;
}

// la classe d'orientation (0..4) : la direction de l'axe du LOSANGE, modulo 180° (les deux
// moitiés d'un losange ont des axes opposés : à 360° elles prenaient deux teintes et la
// diagonale se voyait), par pas de 36° ; avec le type, dix nuances
export function orientationBin(tr) {
  const mx = (tr.B[0] + tr.C[0]) / 2 - tr.A[0], my = (tr.B[1] + tr.C[1]) / 2 - tr.A[1];
  let a = Math.atan2(my, mx) / Math.PI; a -= Math.floor(a);
  return Math.round(a * 5) % 5;
}
